import { disableElement, enableElement } from './helpers'
import flashMessage from './flash_message'

export default class Spinner {

  /**
   * @param {HTMLElement} element
   * @param {Object} param1
   * @param {HTMLElement?} [param1.buttonElement]
   * @param {string} [param1.loadingClass='-loading']
   * @param {number} [param1.timeout=27_000]
   */
  constructor(element, {
    buttonElement = element,
    loadingClass = '-loading',
    timeout = 27_000,
    onLoading,
    onLoaded
  } = {}) {
    this.element       = element
    this.buttonElement = buttonElement
    this.loadingClass  = loadingClass
    this.timeout       = timeout

    this.onLoading = onLoading
    this.onLoaded  = onLoaded

    this.loaded       = this.loaded.bind(this)
    this.frameMissing = this.frameMissing.bind(this)
    this.frameLoaded  = this.frameLoaded.bind(this)
    this.streamLoaded = this.streamLoaded.bind(this)
  }

  destroy() {
    this.#removeListeners()
  }

  loading(event) {
    if (this.alreadyLoading) {
      event.preventDefault()
      event.stopImmediatePropagation()
      return
    }

    this.alreadyLoading = true
    window.addEventListener('turbo:frame-render', this.frameLoaded, { passive: true, once: true })
    this.frame?.addEventListener('turbo:frame-render', this.loaded, { passive: true, once: true })
    window.addEventListener('turbo:before-fetch-response', this.streamLoaded, { passive: true, once: true })
    window.addEventListener('turbo:frame-missing', this.frameMissing, { passive: false, once: true })

    this.buttonElement.classList.add(this.loadingClass)

    this.loadingTimeout = setTimeout(() => {
      this.loaded()
    }, this.timeout)

    // Small timeout to allow the link to serialize and perform its action
    // before removing the href/ disabling
    setTimeout(() => {
      disableElement(this.element)
    }, 13)

    if (this.onLoading) this.onLoading(event)
  }

  loaded() {
    this.#removeListeners()
    this.alreadyLoading = false

    if (this.loadingTimeout) {
      clearTimeout(this.loadingTimeout)
      this.loadingTimeout = null
    }

    enableElement(this.element)

    this.buttonElement.classList.remove(this.loadingClass)

    if (this.onLoaded) this.onLoaded()
  }

  async frameMissing(event) {
    const { target, detail: { response } } = event
    if ((this.frame && target === this.frame) || (this.href && response.url === this.href)) {
      this.loaded()

      if (!response.ok) {
        event.preventDefault()
        flashMessage(`close|alert|Something went wrong: Error ${response.status}`)
      }
    }
  }

  // ==== Getters

  get frameId() {
    return this.element.dataset.turboFrame ||
      this.element.closest('turbo-frame')?.id
  }

  get frame() {
    if (this.element.dataset.turboFrame) {
      return document.getElementById(this.frameId)
    }
    if (this.element.dataset.remote === 'true') return null

    return this.element.closest('turbo-frame') ?? window
  }

  get form() {
    return this.buttonElement.form ?? this.buttonElement.closest('form')
  }

  get href() {
    if (this.buttonElement.nodeName === 'A') {
      return this.buttonElement.href || this.buttonElement.dataset.href
    }

    return this.form?.action
  }

  // ==== Setters

  // ==== Private

  async streamLoaded({ detail: { fetchResponse } }) {
    if (fetchResponse.location.href !== this.href) return
    if (!fetchResponse.contentType.includes('text/vnd.turbo-stream')) return

    const html = await fetchResponse.responseHTML
    if (html.includes('action="redirect"')) return

    this.loaded()
  }

  frameLoaded({ target }) {
    if (this.frameId && this.frameId !== target.id) return

    this.loaded()
  }

  #removeListeners() {
    this.frame?.removeEventListener('turbo:frame-render', this.loaded)
    window.removeEventListener('turbo:fetch-request-error', this.frameMissing)
    window.removeEventListener('turbo:before-fetch-response', this.streamLoaded)
    window.removeEventListener('turbo:frame-render', this.frameLoaded)
  }

}
